import React from "react";
import { IVehicle, ICoverage, IQuoteApiClient, IAddress, emptyAddress } from "../classes/Domain";
import Select, { ValueType, ActionMeta } from "react-select"
import VehicleExplorer from "../classes/VehicleExplorer";
import * as _ from 'lodash';
import { validationUtils } from "../validation/Validation";
import { Form, Row, Col, Button } from 'react-bootstrap';
import Question from "./Question";
import { QUOTE_QUESTIONS, YES_NO } from "../AppConstants";
import {
    getCompCollDeductibleOptions,
    getStateQuoteConfig,
    getVehicleUseOptions,
    DEFAULT_VEHICLE_USE,
    GARAGING_COUNTRY,
} from "../config/StateQuoteConfig";
import { fetchDefaultAnnualMileageByZip } from "../config/defaultMileageByZip";
import { Rifm } from 'rifm';
import Utilities from "../classes/Utilities";

const isEmpty = (str) => (!str?.length);

const VEHICLE_OPTION_TYPES = {
    YEAR: "YEAR",
    MAKE: "MAKE",
    MODEL: "MODEL",
    BODY: "BODY",
    ENGINE: "ENGINE",
    CYLINDERS: "CYLINDERS",
    SPECS: "SPECS",
    FINISH: "FINISH",
    COVERAGES: "COVERAGES"
};

const NO_OPTIONS = "No_options";

const START_YEAR = 1980;

type VehicleOption = {
    value: string,
    label: string,
    type: string
}

type OptionsGroup = {
    label: string,
    options: Array<VehicleOption>;
}

class VinRecord {
    year: number;
    make: string;
    model: string;
    body: string;
    engineSize: string;
    specsCombined: string;
    vin: string;
    cylinders: number;
    antiThft: string;
    compSymbol: string;
    collSymbol: string;
    costNewId: string;

    constructor() {
        this.year = 0;
        this.cylinders = 0;
        this.make = "";
        this.model = "";
        this.body = "";
        this.engineSize = "";
        this.specsCombined = "";
        this.vin = "";
        this.antiThft = "";
        this.compSymbol = "";
        this.collSymbol = "";
        this.costNewId = "";
    }
}

function buildCoverageOptions(stateCode: string | null | undefined): Array<VehicleOption> {
    return getCompCollDeductibleOptions(stateCode).map(o => ({
        label: o.label,
        value: o.value,
        type: VEHICLE_OPTION_TYPES.COVERAGES
    }));
}

function buildSelectedOptionsFromVehicle(vehicle: IVehicle, stateCode: string): Array<VehicleOption> {
    if (!vehicle || !(vehicle.year > 0)) {
        return [];
    }

    const selectedOptions: Array<VehicleOption> = [
        { value: vehicle.year.toString(), label: vehicle.year.toString(), type: VEHICLE_OPTION_TYPES.YEAR }
    ];

    if (!vehicle.make) {
        return selectedOptions;
    }
    selectedOptions.push({ value: vehicle.make, label: vehicle.make, type: VEHICLE_OPTION_TYPES.MAKE });

    if (!vehicle.model) {
        return selectedOptions;
    }
    selectedOptions.push({ value: vehicle.model, label: vehicle.model, type: VEHICLE_OPTION_TYPES.MODEL });

    if (!vehicle.body) {
        return selectedOptions;
    }
    selectedOptions.push({ value: vehicle.body, label: vehicle.body, type: VEHICLE_OPTION_TYPES.BODY });

    if (vehicle.engineSize) {
        selectedOptions.push({ value: vehicle.engineSize, label: vehicle.engineSize, type: VEHICLE_OPTION_TYPES.ENGINE });
    }

    if (vehicle.cylinders > 0) {
        selectedOptions.push({
            value: vehicle.cylinders.toString(),
            label: vehicle.cylinders.toString(),
            type: VEHICLE_OPTION_TYPES.CYLINDERS
        });
    }

    // Empty specs still counts as completed cascade step (runtime uses "unspecified").
    const specsValue = vehicle.specs !== "" && vehicle.specs != null ? vehicle.specs : "unspecified";
    if (vehicle.engineSize || vehicle.cylinders > 0 || (vehicle.coverages && vehicle.coverages.length > 0)) {
        selectedOptions.push({ value: specsValue, label: specsValue, type: VEHICLE_OPTION_TYPES.SPECS });
    }

    if (vehicle.coverages && vehicle.coverages.length > 0) {
        const coverageOpts = buildCoverageOptions(stateCode);
        const selected = vehicle.coverages.filter(x => x.isSelected);
        if (selected.length > 0) {
            const limit = coverageOpts.filter(x => String(x.value) === String(selected[0].value));
            if (limit.length > 0) {
                selectedOptions.push(limit[0]);
            } else {
                selectedOptions.push({
                    value: String(selected[0].value),
                    label: String(selected[0].value),
                    type: VEHICLE_OPTION_TYPES.COVERAGES
                });
            }
        } else if (coverageOpts.length > 0) {
            selectedOptions.push(coverageOpts[0]);
        }
    }

    return selectedOptions;
}

function isVehicleYmmComplete(vehicle: IVehicle | null | undefined): boolean {
    return !!(vehicle
        && vehicle.year > 0
        && vehicle.make
        && vehicle.model
        && vehicle.body
        && vehicle.coverages
        && vehicle.coverages.length > 0);
}

interface IProps {
    updatingStore?: boolean,
    quoteUid: string,
    quoteId: number,
    stateCode: string,
    zipCode: string,
    vehicle: IVehicle | null,
    vehicleIndex: number,
    isActive: boolean,
    errors: any,
    quoteApiClient: IQuoteApiClient,
    disabled?: boolean,
    onVehicleChange(vehicle: IVehicle, index: number): void,
    onVehicleDelete(index: number): void,
    getQuickVehicleCoverages: (stateCode?: string) => Array<ICoverage>;
}

interface IState {
    selectedOptions: Array<VehicleOption>,
    menuYearIsOpen: boolean,
    menuMakeIsOpen: boolean,
    menuModelIsOpen: boolean,
    menuBodyIsOpen: boolean,
    menuCoverageIsOpen: boolean,
    loadingOptionsMake: boolean,
    loadingOptionsModel: boolean,
    loadingOptionsBody: boolean,
    loadingOptionsCovs: boolean,
    saveAndContinue: boolean,
    updatingAppState: boolean,
    options: Array<OptionsGroup>,
    propsMerged: boolean,
    isActive: boolean,
    vehicle: IVehicle;
}

class VehicleSelectByStep extends React.Component<IProps, IState> {
    selectYearRef: React.RefObject<Select>;
    selectMakeRef: React.RefObject<Select>;
    selectModelRef: React.RefObject<Select>;
    selectBodyRef: React.RefObject<Select>;
    selectCoverageRef: React.RefObject<Select>;
    vehicleExplorer: VehicleExplorer;
    vinRecords: Array<VinRecord>;
    apiClient: IQuoteApiClient;
    /** Customer changed annual mileage — do not overwrite on zip refresh. */
    private mileageUserEdited = false;
    private mileageLookupZip: string | null = null;

    constructor(props: IProps) {
        super(props);
        this.selectYearRef = React.createRef();
        this.selectMakeRef = React.createRef();
        this.selectModelRef = React.createRef();
        this.selectBodyRef = React.createRef();
        this.selectCoverageRef = React.createRef();
        this.apiClient = this.props.quoteApiClient;
        this.vehicleExplorer = new VehicleExplorer();
        this.vinRecords = [];

        this.yearStepHandler = this.yearStepHandler.bind(this);
        this.makeStepHandler = this.makeStepHandler.bind(this);
        this.modelStepHandler = this.modelStepHandler.bind(this);
        this.bodyStepHandler = this.bodyStepHandler.bind(this);
        this.engineStepHandler = this.engineStepHandler.bind(this);
        this.cylindersStepHandler = this.cylindersStepHandler.bind(this);
        this.specsStepHandler = this.specsStepHandler.bind(this);
        this.coverageStepHandler = this.coverageStepHandler.bind(this);
        this.finishStepHandler = this.finishStepHandler.bind(this);

        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.YEAR, this.yearStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.MAKE, this.makeStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.MODEL, this.modelStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.BODY, this.bodyStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.ENGINE, this.engineStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.CYLINDERS, this.cylindersStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.SPECS, this.specsStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.COVERAGES, this.coverageStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.FINISH, this.finishStepHandler);

        this.state = {
            selectedOptions: [],
            menuYearIsOpen: false,
            menuMakeIsOpen: false,
            menuModelIsOpen: false,
            menuBodyIsOpen: false,
            menuCoverageIsOpen: false,
            loadingOptionsMake: false,
            loadingOptionsModel: false,
            loadingOptionsBody: false,
            loadingOptionsCovs: false,
            saveAndContinue: false,
            updatingAppState: false,
            propsMerged: false,
            options: [],
            isActive: this.props.isActive,
            vehicle: this.props.vehicle ?? {} as IVehicle
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (prevState.propsMerged) return prevState;
        const vehicle = nextProps.vehicle as IVehicle;

        if (vehicle && vehicle.year > 0) {
            return {
                ...prevState,
                vehicle: vehicle,
                selectedOptions: buildSelectedOptionsFromVehicle(vehicle, nextProps.stateCode),
                propsMerged: true
            };
        }

        return prevState;
    }

    componentDidMount() {
        // No garaging street UI: seed ZIP (and keep any saved street/city) into the vehicle model.
        const seeded = this.withQualitasDefaults(this.state.vehicle || {} as IVehicle);
        this.setState({ vehicle: seeded }, () => {
            this.props.onVehicleChange(seeded, this.props.vehicleIndex);
            this.applyDefaultMileageIfNeeded();
        });

        // Returning to the step with a completed vehicle: hydrate selects only — do not re-run YMM cascade
        // (cascade used to rebuild vehicle and drop Qualitas fields: ownership/use/garaging/mileage).
        if (isVehicleYmmComplete(this.state.vehicle)) {
            const coverageOptions = [{ label: "Select coverage", options: buildCoverageOptions(this.props.stateCode) }];
            this.setState({
                options: coverageOptions,
                ...this.menuOpenFlags("none")
            });
            return;
        }

        if (this.state.selectedOptions.length) {
            const lastSelectedOption = this.state.selectedOptions[this.state.selectedOptions.length - 1];

            const nextStep = this.vehicleExplorer.findNextStepFor(lastSelectedOption.type);
            if (nextStep) {
                nextStep.handler(lastSelectedOption.value);
            }
        } else {
            const nextStep = this.vehicleExplorer.findStepByType(VEHICLE_OPTION_TYPES.YEAR);
            if (nextStep) {
                nextStep.handler();
            }
        }
    }

    getVinRecords(year: number, make: string, model: string) {
        if (0 === this.vinRecords.length) {
            return this.apiClient
                .getVinRecords(year, make, model, this.props.quoteUid, null)
                .then((response) => {
                    if (response.data.options && 0 < response.data.options.length) {
                        this.vinRecords = _.map(response.data.options,
                            (o) => {
                                var r = new VinRecord();
                                r.year = o.year;
                                r.make = o.make;
                                r.model = o.model;
                                r.body = o.body;
                                r.engineSize = o.engineSize;
                                r.cylinders = o.cylinders;
                                r.specsCombined = o.specsCombined;
                                r.vin = o.vin;
                                r.antiThft = o.antiThft;
                                r.compSymbol = o.compSymbol;
                                r.collSymbol = o.collSymbol;
                                return r;
                            });
                    }
                    return this.vinRecords;
                });
        } else {
            return (Promise.resolve(this.vinRecords));
        }
    }

    updateGlobalVehicleState(vehicle: IVehicle, index: number, doing: boolean) {
        return new Promise((resolve, reject) => {
            if (doing) {
                this.props.onVehicleChange(this.withQualitasDefaults(vehicle), index);
            }
            resolve();
        });
    }

    withQualitasDefaults(vehicle: IVehicle): IVehicle {
        const garaging = vehicle.garagingAddress ?? {
            ...emptyAddress,
            stateCode: this.props.stateCode || "",
            zipCode: this.props.zipCode || "",
            country: GARAGING_COUNTRY.USA
        } as IAddress;

        // Req #3: a stored Business use must never be rated for TX — fall back to Commute.
        const storedUse = vehicle.vehicleUse || DEFAULT_VEHICLE_USE;
        const vehicleUse = (this.props.stateCode || '').toUpperCase() === 'TX'
            && storedUse === QUOTE_QUESTIONS.VEHICLE_USE.BUISNESS
            ? DEFAULT_VEHICLE_USE
            : storedUse;

        return {
            ...vehicle,
            // Empty use becomes Commute. A stored Farming or Other is kept.
            vehicleUse,
            // Req #2: leased question defaults to No (Own) when unanswered.
            ownership: vehicle.ownership || QUOTE_QUESTIONS.VEHICLE_DETAILS.OWNERSHIP.OWN,
            parkAtHome: vehicle.parkAtHome || QUOTE_QUESTIONS.YES_NO.NO,
            valueMiles: vehicle.valueMiles || "",
            garagingAddress: {
                ...garaging,
                // Qualitas quick vehicle: no street UI; ZIP from zip step drives mileage + rating.
                // Keep any saved street / city / apartment; do not clear them.
                country: garaging.country || GARAGING_COUNTRY.USA,
                stateCode: garaging.stateCode || this.props.stateCode || "",
                zipCode: garaging.zipCode || this.props.zipCode || "",
                city: garaging.city || ""
            }
        };
    }

    applyDefaultMileageIfNeeded = (force = false) => {
        const cfg = getStateQuoteConfig(this.props.stateCode);
        if (!cfg?.requireAnnualMileage) return;

        const zip = (this.state.vehicle?.garagingAddress?.zipCode || this.props.zipCode || "").replace(/\D/g, "").slice(0, 5);
        if (!/^\d{5}$/.test(zip)) return;

        const hasMiles = !!(this.state.vehicle?.valueMiles && String(this.state.vehicle.valueMiles).length);
        // Never overwrite a value the customer typed.
        if (this.mileageUserEdited && hasMiles) return;
        // Remount / revisit with existing miles (incl. prior default): keep them.
        if (!force && hasMiles) return;

        this.mileageLookupZip = zip;
        fetchDefaultAnnualMileageByZip(this.props.stateCode, zip, GARAGING_COUNTRY.USA).then((miles) => {
            if (!miles) return;
            if (this.mileageUserEdited && this.state.vehicle?.valueMiles) return;
            const updated = this.withQualitasDefaults({ ...this.state.vehicle, valueMiles: miles });
            this.setState({ vehicle: updated }, () => {
                this.props.onVehicleChange(updated, this.props.vehicleIndex);
            });
        });
    };

    /** Selected COMP/COLL deductible value for a vehicle; "0" = liability only (default). */
    selectedCoverageValue(vehicle: IVehicle): string {
        const selected = (vehicle.coverages || []).filter(c => c && c.isSelected && (c.code === 'COMP' || c.code === 'COLL'));
        return selected.length > 0 ? String(selected[0].value) : "0";
    }

    selectedCoverageLabel(vehicle: IVehicle): string {
        const value = this.selectedCoverageValue(vehicle);
        const match = getCompCollDeductibleOptions(this.props.stateCode).find(o => o.value === value);
        return match ? match.label : value;
    }

    /** Req #1: coverage buttons write the same COMP/COLL selection the old dropdown wrote. */
    applyCoverageSelection(selectedValue: string) {
        let updatedVehicle = Object.assign({}, { ...this.state.vehicle, coverages: this.state.vehicle.coverages ?? this.props.getQuickVehicleCoverages(this.props.stateCode) });

        let coverageChanged = this.state.vehicle.coverages ? false : true;
        for (let i = 0; i < updatedVehicle.coverages.length; i++) {
            let cov = updatedVehicle.coverages[i];
            if (cov.code === 'COMP' || cov.code === 'COLL') {
                const isSelected = String(cov.value) === selectedValue;
                if (cov.isSelected !== isSelected) {
                    coverageChanged = true;
                }
                cov.isSelected = isSelected;
            }
        }

        this.updateGlobalVehicleState(updatedVehicle, this.props.vehicleIndex, coverageChanged).then(() => {
            this.setState({ vehicle: updatedVehicle, ...this.menuOpenFlags("none") });
        });
    }

    onCoverageChange = (label: string) => {
        const match = getCompCollDeductibleOptions(this.props.stateCode).find(o => o.label === label);
        if (match) {
            this.applyCoverageSelection(match.value);
        }
    };

    onVehicleUseSelect = (option: ValueType<{ value: string; label: string }, false>) => {
        const value = option ? (option as { value: string }).value : "";
        if (!value) return;
        const updated = this.withQualitasDefaults({ ...this.state.vehicle, vehicleUse: value });
        this.setState({ vehicle: updated }, () => {
            this.props.onVehicleChange(updated, this.props.vehicleIndex);
        });
    };

    leasedAnswer(ownership: string | null | undefined): string {
        if (ownership === QUOTE_QUESTIONS.VEHICLE_DETAILS.OWNERSHIP.LEASE) {
            return YES_NO.YES;
        }
        if (ownership === QUOTE_QUESTIONS.VEHICLE_DETAILS.OWNERSHIP.OWN) {
            return YES_NO.NO;
        }
        return "";
    }

    onLeasedChange = (answer: string) => {
        const leased = answer === YES_NO.YES;
        const updated = this.withQualitasDefaults({
            ...this.state.vehicle,
            ownership: leased
                ? QUOTE_QUESTIONS.VEHICLE_DETAILS.OWNERSHIP.LEASE
                : QUOTE_QUESTIONS.VEHICLE_DETAILS.OWNERSHIP.OWN,
            payments: leased ? "" : this.state.vehicle.payments
        });
        this.setState({ vehicle: updated }, () => {
            this.props.onVehicleChange(updated, this.props.vehicleIndex);
        });
    };

    onMileageChange = (value: string) => {
        this.mileageUserEdited = true;
        const updated = this.withQualitasDefaults({ ...this.state.vehicle, valueMiles: value });
        this.setState({ vehicle: updated }, () => {
            this.props.onVehicleChange(updated, this.props.vehicleIndex);
        });
    };

    yearStepHandler() {
        const options: VehicleOption[] = [];
        for (let year = 2026; year >= START_YEAR; year--) { //TODO: Get From DB!
            options.push({
                value: year.toString(),
                label: year.toString(),
                type: VEHICLE_OPTION_TYPES.YEAR
            });
        }

        const yearOptions = [{ label: "Select car year", options: options }];
        // Do not wipe vehicle — Qualitas fields (ownership, garaging, use, mileage) live on the same object.
        this.setState({ ...this.state, options: yearOptions, menuYearIsOpen: this.state.menuYearIsOpen });
    }

    makeStepHandler(prevStepSelectedValue) {
        // Year stays focused after a choice. A key there, once its menu is closed, calls onMenuOpen and restarts the year step.
        this.selectYearRef.current?.blur();
        const checkValue = this.state.vehicle?.year ?? 0;
        const vehicleYear = parseInt(prevStepSelectedValue, 10) || checkValue;
        // Preserve Qualitas fields already entered (leased, use, garaging, mileage).
        let updatedVehicle = Object.assign({}, this.state.vehicle || {} as IVehicle, { year: vehicleYear } as IVehicle);

        if (updatedVehicle.year !== checkValue) {
            updatedVehicle.make = "";
            updatedVehicle.model = "";
            updatedVehicle.body = "";
            updatedVehicle.engineSize = "";
            updatedVehicle.cylinders = 0;
            updatedVehicle.specs = "";
            updatedVehicle.coverages = null;
            updatedVehicle.vin = "";
        }

        this.updateGlobalVehicleState(updatedVehicle, this.props.vehicleIndex, updatedVehicle.year !== checkValue).then(() => {
            this.setState({ ...this.state, vehicle: updatedVehicle, loadingOptionsMake: true, ...this.menuOpenFlags("none") }, () => {
                this.apiClient
                    .getVehicleMakes(vehicleYear, this.props.quoteId, this.props.quoteUid)
                    .then((response) => {
                        var makeOptions = [
                            {
                                label: "Select make",
                                options: response.data.options.map(obj => ({
                                    ...obj, type: VEHICLE_OPTION_TYPES.MAKE
                                }))
                            }
                        ];

                        this.setState({ ...this.state, options: makeOptions, loadingOptionsMake: false, ...this.menuOpenFlags("make") }, () => {
                            this.selectMakeRef.current?.focus();
                        });
                    });
            });
        });
    }

    modelStepHandler(prevStepSelectedValue) {
        this.selectMakeRef.current?.blur();
        let updatedVehicle = Object.assign({}, this.state.vehicle);
        updatedVehicle.make = prevStepSelectedValue;
        updatedVehicle.model = '';
        const checkValue = this.state.vehicle?.make ?? '';

        this.updateGlobalVehicleState(updatedVehicle, this.props.vehicleIndex, updatedVehicle.make !== checkValue).then(() => {
            this.setState({ ...this.state, vehicle: updatedVehicle, loadingOptionsModel: true, ...this.menuOpenFlags("none") }, () => {
                this.apiClient
                    .getVehicleModels(this.state.vehicle.year, prevStepSelectedValue, this.props.quoteUid)
                    .then((response) => {
                        var modelOptions = [
                            {
                                label: "Select model",
                                options: response.data.options.map(obj => ({ ...obj, type: VEHICLE_OPTION_TYPES.MODEL }))
                            }
                        ];

                        this.setState({ ...this.state, options: modelOptions, loadingOptionsModel: false, ...this.menuOpenFlags("model") }, () => {
                            this.selectModelRef.current?.focus();
                        });
                    });
            });
        });
    }

    bodyStepHandler(prevStepSelectedValue) {
        this.selectModelRef.current?.blur();
        let updatedVehicle = Object.assign({}, this.state.vehicle);
        updatedVehicle.model = prevStepSelectedValue;
        updatedVehicle.body = "";
        updatedVehicle.engineSize = "";
        updatedVehicle.cylinders = 0;
        updatedVehicle.specs = "";
        updatedVehicle.coverages = null;
        updatedVehicle.vin = "";
        const checkValue = this.state.vehicle?.model ?? '';

        this.updateGlobalVehicleState(updatedVehicle, this.props.vehicleIndex, updatedVehicle.model !== checkValue).then(() => {
            this.setState({ ...this.state, vehicle: updatedVehicle, loadingOptionsBody: true, ...this.menuOpenFlags("none") }, () => {
                this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, prevStepSelectedValue)
                    .then(() => {
                        if (0 < this.vinRecords.length) {
                            const bodyGroups = _.groupBy(this.vinRecords, "body");
                            const bodyTypes = _.map(bodyGroups, (records, bodyType) => {
                                return {
                                    body: bodyType,
                                    count: records.length
                                }
                            });

                            if (1 === bodyTypes.length) {
                                const bodyTypeOption = { value: bodyTypes[0].body, label: bodyTypes[0].body, type: VEHICLE_OPTION_TYPES.BODY };
                                const extendedSelectedOptions = [...this.state.selectedOptions, bodyTypeOption];
                                this.setState({ selectedOptions: extendedSelectedOptions, loadingOptionsBody: false, ...this.menuOpenFlags("none") }, () => {
                                    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.BODY, bodyTypeOption.value)) {
                                        this.handleError();
                                    }
                                });
                            } else if (1 < bodyTypes.length) {
                                const bodyTypeOptions = [
                                    {
                                        label: "Select body type",
                                        options: bodyTypes.map(t => ({ value: t.body, label: t.body, type: VEHICLE_OPTION_TYPES.BODY }))
                                    }
                                ];

                                this.setState({ ...this.state, options: bodyTypeOptions, loadingOptionsBody: false, ...this.menuOpenFlags("body") });
                            }
                        }
                        else {
                            this.handleNoVehicleOptions();
                        }
                    });
            });
        });
    }

    engineStepHandler(prevStepSelectedValue) {
        let updatedVehicle = Object.assign({}, this.state.vehicle);
        updatedVehicle.body = prevStepSelectedValue;
        updatedVehicle.vin = "";
        const checkValue = this.state.vehicle?.body ?? '';

        this.updateGlobalVehicleState(updatedVehicle, this.props.vehicleIndex, updatedVehicle.body !== checkValue).then(() => {
            this.setState({ vehicle: updatedVehicle }, () => {
                //this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, this.state.vehicle.model)
                //    .then(() => {
                //        if (0 < this.vinRecords.length) {
                //            const records = _.filter(this.vinRecords, (record) => record.body === prevStepSelectedValue);
                //            const engineGroups = _.groupBy(records, "engineSize");
                //            const engineSizes = _.map(engineGroups, (coll, engineSize) => {
                //                return {
                //                    value: engineSize,
                //                    label: engineSize,
                //                    type: VEHICLE_OPTION_TYPES.ENGINE
                //                }
                //            });

                //            if (1 <= engineSizes.length) {
                //                const engineOption = engineSizes.sort((a, b) => (parseFloat(a.value) < parseFloat(b.value) ? -1 : 1))[0];
                //                const extendedSelectedOptions = [...this.state.selectedOptions, engineOption];
                //                this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                //                    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.ENGINE, engineOption.value)) {
                //                        this.handleError();
                //                    }
                //                });
                //            } else {
                //                this.handleNoVehicleOptions();
                //            }
                //        }
                //        else {
                //            this.handleNoVehicleOptions();
                //        }
                //    });

                const engineOption = {
                    value: "2",
                    label: "2",
                    type: VEHICLE_OPTION_TYPES.ENGINE
                };
                const extendedSelectedOptions = [...this.state.selectedOptions, engineOption];
                this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.ENGINE, engineOption.value)) {
                        this.handleError();
                    }
                });
            });
        });
    }

    cylindersStepHandler(prevStepSelectedValue) {
        let updatedVehicle = Object.assign({}, this.state.vehicle);
        updatedVehicle.engineSize = prevStepSelectedValue;
        updatedVehicle.vin = "";
        const checkValue = this.state.vehicle?.engineSize ?? '';

        this.updateGlobalVehicleState(updatedVehicle, this.props.vehicleIndex, updatedVehicle.engineSize !== checkValue).then(() => {
            this.setState({ vehicle: updatedVehicle }, () => {
                //this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, this.state.vehicle.model)
                //    .then(() => {
                //        if (0 < this.vinRecords.length) {
                //            const records = _.filter(this.vinRecords, (record) => record.body === this.state.vehicle.body
                //                && record.engineSize === prevStepSelectedValue);

                //            const cylinderGroups = _.groupBy(records, "cylinders");
                //            const cylinders = _.map(cylinderGroups, (coll, cylinderCount) => {
                //                return {
                //                    value: cylinderCount.toString(),
                //                    label: cylinderCount.toString(),
                //                    type: VEHICLE_OPTION_TYPES.CYLINDERS
                //                }
                //            });

                //            if (1 <= cylinders.length) {
                //                const cylinderOption = cylinders.sort((a, b) => (parseFloat(a.value) < parseFloat(b.value) ? -1 : 1))[0];
                //                const extendedSelectedOptions = [...this.state.selectedOptions, cylinderOption];
                //                this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                //                    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.CYLINDERS, cylinderOption.value)) {
                //                        this.handleError();
                //                    }
                //                });
                //            }
                //            else {
                //                this.handleNoVehicleOptions();
                //            }
                //        }
                //        else {
                //            this.handleNoVehicleOptions();
                //        }
                //    });
                const cylinderOption = {
                    value: "4",
                    label: "4",
                    type: VEHICLE_OPTION_TYPES.CYLINDERS
                };
                const extendedSelectedOptions = [...this.state.selectedOptions, cylinderOption];
                this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.CYLINDERS, cylinderOption.value)) {
                        this.handleError();
                    }
                });
            });
        });
    }

    specsStepHandler(prevStepSelectedValue) {
        let updatedVehicle = Object.assign({}, this.state.vehicle);
        updatedVehicle.cylinders = parseInt(prevStepSelectedValue, 10);
        updatedVehicle.vin = "";
        const checkValue = this.state.vehicle?.cylinders ?? 0;

        this.updateGlobalVehicleState(updatedVehicle, this.props.vehicleIndex, updatedVehicle.cylinders !== checkValue).then(() => {
            this.setState({ vehicle: updatedVehicle }, () => {
                //this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, this.state.vehicle.model)
                //    .then(() => {
                //        if (0 < this.vinRecords.length) {
                //            const records = _.filter(this.vinRecords, (record) => record.body === this.state.vehicle.body
                //                && record.engineSize === this.state.vehicle.engineSize
                //                && record.cylinders === parseInt(prevStepSelectedValue, 10)
                //                && record.specsCombined.length > 0);

                //            if (0 === records.length) {
                //                const specOption = { value: "", label: "", type: VEHICLE_OPTION_TYPES.SPECS };
                //                const extendedSelectedOptions = [...this.state.selectedOptions, specOption];
                //                this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                //                    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.SPECS, "")) {
                //                        this.handleError();
                //                    }
                //                });

                //                return;
                //            }

                //            const specsGroups = _.groupBy(records, "specsCombined");
                //            const specs = _.map(specsGroups, (coll, spec) => {
                //                return {
                //                    value: spec,
                //                    label: spec,
                //                    type: VEHICLE_OPTION_TYPES.SPECS
                //                }
                //            });

                //            if (0 < specs.length) {
                //                const specOption = specs[0];
                //                const extendedSelectedOptions = [...this.state.selectedOptions, specOption];
                //                this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                //                    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.SPECS, specOption.value)) {
                //                        this.handleError();
                //                    }
                //                });
                //            }
                //            else {
                //                this.handleNoVehicleOptions();
                //            }
                //        }
                //        else {
                //            this.handleNoVehicleOptions();
                //        }
                //    });
                const specOption = {
                    value: "unspecified",
                    label: "unspecified",
                    type: VEHICLE_OPTION_TYPES.SPECS
                };
                const extendedSelectedOptions = [...this.state.selectedOptions, specOption];
                this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.SPECS, specOption.value)) {
                        this.handleError();
                    }
                });
            });
        });
    }

    coverageStepHandler(prevStepSelectedValue) {
        let updatedVehicle = Object.assign({}, this.state.vehicle);
        updatedVehicle.specs = prevStepSelectedValue;

        let checkValue = this.state.vehicle?.specs ?? '';
        if ((updatedVehicle.specs === '' || updatedVehicle.specs === undefined)
            && (this.state.vehicle?.specs === '' || this.state.vehicle?.specs === undefined)) {
            updatedVehicle.specs = 'unspecified';
        }

        this.updateGlobalVehicleState(updatedVehicle, this.props.vehicleIndex, updatedVehicle.specs !== checkValue).then(() => {
            this.setState({ ...this.state, loadingOptionsCovs: true, vehicle: updatedVehicle, ...this.menuOpenFlags("none") }, () => {
                const coverageOptions = [{ label: "Select coverage", options: buildCoverageOptions(this.props.stateCode) }];

                if (updatedVehicle.vin) {
                    if (updatedVehicle.coverages) {
                        this.setState({ ...this.state, loadingOptionsCovs: false, options: coverageOptions, ...this.menuOpenFlags("coverage") });
                    }
                    else {
                        const defaultCoverageOption = { value: coverageOptions[0].options[0].value, label: coverageOptions[0].options[0].label, type: VEHICLE_OPTION_TYPES.COVERAGES };
                        const extendedSelectedOptions = [...this.state.selectedOptions, defaultCoverageOption];

                        this.setState({ loadingOptionsCovs: false, vehicle: updatedVehicle, options: coverageOptions, selectedOptions: extendedSelectedOptions, ...this.menuOpenFlags("none") }, () => {
                            if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.COVERAGES, defaultCoverageOption.value)) {
                                this.handleError();
                            }
                        });
                    }
                }
                else {
                    this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, this.state.vehicle.model)
                        .then(() => {
                            if (0 < this.vinRecords.length) {
                                let records: Array<VinRecord> = _.filter(this.vinRecords, (record) => record.body === this.state.vehicle.body);

                                //if (this.state.vehicle.specs === 'unspecified') {
                                //    records = _.filter(this.vinRecords, (record) => record.body === this.state.vehicle.body
                                //        && record.engineSize === this.state.vehicle.engineSize
                                //        && record.cylinders === this.state.vehicle.cylinders);
                                //}
                                //else {
                                //    records = _.filter(this.vinRecords, (record) => record.body === this.state.vehicle.body
                                //        && record.engineSize === this.state.vehicle.engineSize
                                //        && record.cylinders === this.state.vehicle.cylinders
                                //        && record.specsCombined === this.state.vehicle.specs);
                                //}

                                if (0 === records.length) {
                                    this.handleNoVehicleOptions();
                                } else {
                                    updatedVehicle.vin = records[0].vin;

                                    if (updatedVehicle.coverages) {
                                        this.setState({ ...this.state, loadingOptionsCovs: false, vehicle: updatedVehicle, options: coverageOptions, ...this.menuOpenFlags("coverage") });
                                    }
                                    else {
                                        const defaultCoverageOption = { value: coverageOptions[0].options[0].value, label: coverageOptions[0].options[0].label, type: VEHICLE_OPTION_TYPES.COVERAGES };
                                        const extendedSelectedOptions = [...this.state.selectedOptions, defaultCoverageOption];

                                        this.setState({ loadingOptionsCovs: false, vehicle: updatedVehicle, options: coverageOptions, selectedOptions: extendedSelectedOptions, ...this.menuOpenFlags("none") }, () => {
                                            if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.COVERAGES, defaultCoverageOption.value)) {
                                                this.handleError();
                                            }
                                        });
                                    }
                                }
                            }
                            else {
                                this.handleNoVehicleOptions();
                            }
                        });
                }
            });
        });
    }

    finishStepHandler(prevStepSelectedValue) {
        this.applyCoverageSelection(String(prevStepSelectedValue));
    }

    handleNoVehicleOptions() {
        this.setState({
            ...this.state,
            options: [{
                label: "Please try another vehicle",
                options: [{ label: "No options", value: NO_OPTIONS, type: "" }]
            }],
            loadingOptionsMake: false,
            loadingOptionsModel: false,
            loadingOptionsBody: false,
            loadingOptionsCovs: false,
            ...this.menuOpenFlags("none")
        });
    }

    handleError() {
        alert("ERROR !!!");
    }

    handleOpen(type: string) {
        this.vinRecords = [];
        const removedValueType = type;
        const reducedSelectedOptions = [...this.state.selectedOptions];

        for (let i = 0; i < reducedSelectedOptions.length; i++) {
            if (reducedSelectedOptions[i].type === removedValueType) {
                reducedSelectedOptions.splice(i, reducedSelectedOptions.length);
                break;
            }
        }

        this.setState({ selectedOptions: reducedSelectedOptions, saveAndContinue: false }, () => {
            const newVehicleStep = this.vehicleExplorer.findStepByType(removedValueType);
            const lastSelectedValue = 0 < reducedSelectedOptions.length
                ? reducedSelectedOptions[reducedSelectedOptions.length - 1].value
                : "";
            if (newVehicleStep) {
                newVehicleStep.handler(lastSelectedValue);
            } else {
                this.handleError();
            }
        });
    }

    handleChange = (newValues: ValueType<any>, action: ActionMeta<VehicleOption>) => {
        const lastSelectedOption = newValues;
        if (NO_OPTIONS === lastSelectedOption.value) return;
        const extendedSelectedOptions = [...this.state.selectedOptions, lastSelectedOption];
        this.setState({ selectedOptions: extendedSelectedOptions }, () => {
            if (!this.vehicleExplorer.handleNextStepFor(lastSelectedOption.type, lastSelectedOption.value)) {
                this.handleError();
            }
        });
    };

    formatMakeIconName(make: string) {
        return make.replace(/\s/g, "_");
    }

    /** Only one react-select menu open at a time (SMCI latent bug: prior flags stayed true). */
    private menuOpenFlags(open: "year" | "make" | "model" | "body" | "coverage" | "none") {
        return {
            menuYearIsOpen: open === "year",
            menuMakeIsOpen: open === "make",
            menuModelIsOpen: open === "model",
            menuBodyIsOpen: open === "body",
            menuCoverageIsOpen: open === "coverage"
        };
    }

    openYearMenu = () => {
        this.setState({ ...this.menuOpenFlags("year"), isActive: true }, () => { this.selectYearRef.current?.focus(); });
    };

    openMakeMenu = () => {
        this.setState({ ...this.menuOpenFlags("make"), isActive: true }, () => { this.selectMakeRef.current?.focus(); });
    };

    openModelMenu = () => {
        this.setState({ ...this.menuOpenFlags("model"), isActive: true }, () => { this.selectModelRef.current?.focus(); });
    };

    openBodyMenu = () => {
        this.setState({ ...this.menuOpenFlags("body"), isActive: true }, () => { this.selectBodyRef.current?.focus(); });
    };

    openCoverageMenu = () => {
        this.setState({ ...this.menuOpenFlags("coverage"), isActive: true }, () => { this.selectCoverageRef.current?.focus(); });
    };

    render() {
        const requireMileage = getStateQuoteConfig(this.props.stateCode)?.requireAnnualMileage === true;
        const vehicle = this.withQualitasDefaults(this.state.vehicle || {} as IVehicle);
        const vehicleUseOptions = getVehicleUseOptions(this.props.stateCode);
        const coverageItems = getCompCollDeductibleOptions(this.props.stateCode).map(o => o.label);

        return (
            <div className="vehicle-row">
                <Row>
                    <Col sm={2}>
                        <Select
                            ref={this.selectYearRef}
                            options={this.state.options.filter(x => x.options.filter(o => o.type === VEHICLE_OPTION_TYPES.YEAR).length > 0)}
                            value={this.state.selectedOptions.filter(x => x.type === VEHICLE_OPTION_TYPES.YEAR)[0]}
                            placeholder={"Year..."}
                            onFocus={this.openYearMenu}
                            menuIsOpen={this.state.menuYearIsOpen && this.state.isActive}
                            onMenuOpen={() => { this.handleOpen(VEHICLE_OPTION_TYPES.YEAR) }}
                            onChange={this.handleChange}
                            isSearchable={true}
                            blurInputOnSelect={false}
                            autoFocus={false}
                            isClearable={false}
                            isDisabled={this.props.updatingStore || this.props.disabled}
                        />
                        {
                            null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.year") && !this.state.vehicle.year
                                ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "vehicle.year")}
                                </Form.Control.Feedback>
                                : null
                        }
                    </Col>
                    <Col sm={2}>
                        <Select
                            ref={this.selectMakeRef}
                            options={this.state.options.filter(x => x.options.filter(o => o.type === VEHICLE_OPTION_TYPES.MAKE).length > 0)}
                            value={this.state.selectedOptions.filter(x => x.type === VEHICLE_OPTION_TYPES.MAKE)[0]}
                            placeholder={"Make..."}
                            onFocus={this.openMakeMenu}
                            menuIsOpen={this.state.menuMakeIsOpen && this.state.isActive}
                            onMenuOpen={() => { this.handleOpen(VEHICLE_OPTION_TYPES.MAKE) }}
                            onChange={this.handleChange}
                            isSearchable={true}
                            blurInputOnSelect={false}
                            autoFocus={false}
                            isClearable={false}
                            isLoading={this.state.loadingOptionsMake}
                            isDisabled={this.props.updatingStore || this.props.disabled || this.state.menuYearIsOpen || this.state.selectedOptions.filter(x => x.type === VEHICLE_OPTION_TYPES.YEAR).length === 0}
                        />
                        {
                            null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.make") && isEmpty(this.state.vehicle.make)
                                ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "vehicle.make")}
                                </Form.Control.Feedback>
                                : null
                        }
                    </Col>
                    <Col sm={2}>
                        <Select
                            ref={this.selectModelRef}
                            options={this.state.options.filter(x => x.options.filter(o => o.type === VEHICLE_OPTION_TYPES.MODEL).length > 0)}
                            value={this.state.selectedOptions.filter(x => x.type === VEHICLE_OPTION_TYPES.MODEL)[0]}
                            placeholder={"Model..."}
                            onFocus={this.openModelMenu}
                            menuIsOpen={this.state.menuModelIsOpen && this.state.isActive}
                            onMenuOpen={() => { this.handleOpen(VEHICLE_OPTION_TYPES.MODEL) }}
                            onChange={this.handleChange}
                            isSearchable={true}
                            blurInputOnSelect={false}
                            autoFocus={false}
                            isClearable={false}
                            isLoading={this.state.loadingOptionsModel}
                            isDisabled={this.props.updatingStore || this.props.disabled || this.state.menuYearIsOpen || this.state.menuMakeIsOpen || this.state.selectedOptions.filter(x => x.type === VEHICLE_OPTION_TYPES.MAKE).length === 0}
                        />
                        {
                            null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.model") && isEmpty(this.state.vehicle.model)
                                ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "vehicle.model")}
                                </Form.Control.Feedback>
                                : null
                        }
                    </Col>
                    <Col sm={2}>
                        <Select
                            ref={this.selectBodyRef}
                            options={this.state.options.filter(x => x.options.filter(o => o.type === VEHICLE_OPTION_TYPES.BODY).length > 0)}
                            value={this.state.selectedOptions.filter(x => x.type === VEHICLE_OPTION_TYPES.BODY)[0]}
                            placeholder={"Body..."}
                            onFocus={this.openBodyMenu}
                            menuIsOpen={this.state.menuBodyIsOpen && this.state.isActive}
                            onMenuOpen={() => { this.handleOpen(VEHICLE_OPTION_TYPES.BODY) }}
                            onChange={this.handleChange}
                            isSearchable={false}
                            blurInputOnSelect={false}
                            autoFocus={false}
                            isClearable={false}
                            isLoading={this.state.loadingOptionsBody}
                            isDisabled={this.props.updatingStore || this.props.disabled || this.state.menuYearIsOpen || this.state.menuMakeIsOpen || this.state.menuModelIsOpen || this.state.selectedOptions.filter(x => x.type === VEHICLE_OPTION_TYPES.MODEL).length === 0}
                        />
                        {
                            null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.body") && isEmpty(this.state.vehicle.body)
                                ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "vehicle.body")}
                                </Form.Control.Feedback>
                                : null
                        }
                    </Col>
                    <Col sm={3}>
                        <Select
                            options={vehicleUseOptions as Array<{ value: string; label: string }>}
                            value={vehicleUseOptions.find(o => o.value === vehicle.vehicleUse) || null}
                            placeholder={"Vehicle Use..."}
                            onChange={this.onVehicleUseSelect}
                            isSearchable={false}
                            isClearable={false}
                            isDisabled={this.props.updatingStore || this.props.disabled}
                        />
                        {
                            null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.vehicleUse")
                                ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "vehicle.vehicleUse")}
                                </Form.Control.Feedback>
                                : null
                        }
                    </Col>
                    <Col sm={1}>
                        {
                            this.props.vehicleIndex > 0
                                ?
                                <Button
                                    className="btn-remove"
                                    onClick={() => { this.props.onVehicleDelete(this.props.vehicleIndex) }}
                                    disabled={this.props.updatingStore}>
                                    <i className="fas fa-trash"></i>
                                </Button>
                                :
                                null
                        }
                    </Col>
                </Row>

                <Row>
                    <Col sm={12}>
                        <Question
                            name={`coverages-${this.props.vehicleIndex}`}
                            className="vehicle-question"
                            lable="Select vehicle coverages"
                            items={coverageItems}
                            selectedValue={this.selectedCoverageLabel(vehicle)}
                            selected={this.onCoverageChange}
                            disabled={this.props.updatingStore || this.props.disabled}
                            error={
                                null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.coverages") && !vehicle.coverages
                                    ? validationUtils.getValidationErrorFor(this.props.errors, "vehicle.coverages")
                                    : ""
                            }
                        />
                        {
                            vehicle.vehicleUse === QUOTE_QUESTIONS.VEHICLE_USE.BUISNESS
                                ? <div className="vehicle-use-note">
                                    <i className="fas fa-info-circle" aria-hidden="true"></i>
                                    <span>No rideshare or deliveries allowed</span>
                                </div>
                                : null
                        }
                    </Col>
                </Row>

                <Row>
                    <Col sm={12}>
                        <Question
                            name={`leased-${this.props.vehicleIndex}`}
                            className="vehicle-question"
                            lable="Is this vehicle leased?"
                            items={[YES_NO.YES, YES_NO.NO]}
                            selectedValue={this.leasedAnswer(vehicle.ownership)}
                            selected={this.onLeasedChange}
                            disabled={this.props.updatingStore || this.props.disabled}
                            error={
                                null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.ownership")
                                    ? validationUtils.getValidationErrorFor(this.props.errors, "vehicle.ownership")
                                    : ""
                            }
                        />
                    </Col>
                </Row>

                {
                    requireMileage
                        ? <Row>
                            <Col sm={3}>
                                <Form.Group className="vehicle-mileage">
                                    <Form.Label>Estimated Annual mileage</Form.Label>
                                    <Rifm
                                        accept={/\d/g}
                                        format={Utilities.formatNumber}
                                        value={vehicle.valueMiles || ""}
                                        onChange={(v: any) => this.onMileageChange(v.toString().replace(/[^\d]/gi, ""))}>
                                        {({ value, onChange }) => (
                                            <Form.Control
                                                value={value}
                                                disabled={this.props.updatingStore || this.props.disabled}
                                                onChange={onChange}
                                                isInvalid={null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.valueMiles") && isEmpty(vehicle.valueMiles)}
                                                pattern="[0-9]*"
                                                inputMode="numeric"
                                            />
                                        )}
                                    </Rifm>
                                    {
                                        null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "vehicle.valueMiles")
                                            ? <Form.Control.Feedback type="invalid">
                                                {validationUtils.getValidationErrorFor(this.props.errors, "vehicle.valueMiles")}
                                            </Form.Control.Feedback>
                                            : null
                                    }
                                </Form.Group>
                            </Col>
                        </Row>
                        : null
                }
            </div>
        );
    }
}

export default VehicleSelectByStep
